import type {
  ButtonHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  TouchEvent,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type PopperJsVirtualElementCompatibleType = {
  getBoundingClientRect: () => DOMRect;
};

type Props = {
  /** Optionally disable context menu state changes. */
  disabled?: ButtonHTMLAttributes<HTMLButtonElement>['disabled'];
  /** Set by default as true. Allow on menu close to focus on what was selected before. */
  focusInitialEl?: boolean;
  /**
   * ref for the scroll container
   */
  refForScrollContainer?: RefObject<HTMLElement | null>;
  /**
   * optional boolean to dismiss the context menu on scroll
   */
  dismissOnScroll?: boolean;
};

export type ContextMenuEvent =
  | KeyboardEvent<HTMLElement>
  | MouseEvent<HTMLElement>
  | TouchEvent<HTMLElement>;

/**
 * Hook returning Vapor `Menu` props defining a right-click menu for actions
 * against a selection of data, such as in the Account Settings tables and
 * Finder. If you need Finder-style context menus then use the return value
 * `contextMenuAnchorPosition` and note the following:
 *
 * - Menu opens at the pointer's position.
 * - Position teleports instantly with no animation if the menu is already open
 *   when triggered from a different pointer location.
 * - Provide an optional parent ref to automatically close the menu on scroll.
 *
 * @example
 *  ```jsx
 *  const UsageExample = () => {
 *    const [selectedItemId, setSelectedItemId] = useState(null);
 *    const {
 *      contextMenuAnchorPosition,
 *      handleContextMenuEvent,
 *      isContextMenuOpen,
 *      setIsContextMenuOpen,
 *    } = useDataContextMenu();
 *
 *    return (
 *      <>
 *        {items.map((item) => (
 *          <Row
 *            key={item.id}
 *            name={item.name}
 *            onContextMenu={(event) =>
 *              setSelectedItemId(item.id)
 *              handleContextMenuEvent(event)
 *            }
 *          />
 *        ))}
 *
 *        <Menu
 *          anchorPosition={contextMenuAnchorPosition}
 *          content={
 *            <MyContent count={1} ids={[selectedItemId] />}
 *          }
 *          isShowing={isContextMenuOpen}
 *          onRequestChange={setIsContextMenuOpen}
 *        />
 *      </>
 *    );
 *  };
 *  ```
 */
export function useDataContextMenu(props?: Props) {
  const {
    disabled = false,
    focusInitialEl = true,
    refForScrollContainer,
    dismissOnScroll = true,
  } = props || {};

  const [contextMenuAnchorPosition, setContextMenuAnchorPosition] =
    useState<PopperJsVirtualElementCompatibleType>(undefined);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

  const handleContextMenuEvent = useCallback(
    (event: ContextMenuEvent) => {
      // if context menu is disabled, show the browser default context menu
      if (disabled) return;

      // FIND-1219: Conditionally prevent default context menu if the event is a keyboard/mouse/touch event
      event.preventDefault?.();
      event.stopPropagation?.();

      const rect = (event?.target as HTMLElement)?.getBoundingClientRect?.();
      const x = (event as MouseEvent).pageX ?? rect.width / 2 + rect.left;
      const y = (event as MouseEvent).pageY ?? rect.height / 2 + rect.top;

      const nextContextMenuAnchorPosition: PopperJsVirtualElementCompatibleType =
        {
          getBoundingClientRect: () =>
            ({
              bottom: y,
              height: 0,
              left: x,
              right: x,
              top: y,
              width: 0,
            }) as DOMRect,
        };

      setIsContextMenuOpen((prevIsContextMenuOpen) => {
        if (disabled) {
          return prevIsContextMenuOpen;
        }

        if (prevIsContextMenuOpen) {
          setTimeout(() => {
            setContextMenuAnchorPosition(nextContextMenuAnchorPosition);
            setIsContextMenuOpen(true);
          }, 0);
          setContextMenuAnchorPosition(null);
          return false;
        }

        setContextMenuAnchorPosition(nextContextMenuAnchorPosition);
        return true;
      });
    },
    [disabled]
  );

  const initialActiveElement = useRef(null);

  useEffect(() => {
    if (isContextMenuOpen) {
      initialActiveElement.current = document.activeElement as HTMLElement;
    } else if (focusInitialEl) {
      initialActiveElement.current?.focus();
    }
  }, [isContextMenuOpen, initialActiveElement, focusInitialEl]);

  useEffect(() => {
    const handleOnScroll = () =>
      setIsContextMenuOpen((prev) => (prev && dismissOnScroll ? false : null));
    const ref = refForScrollContainer?.current;
    ref?.addEventListener('scroll', handleOnScroll, { passive: true });
    return () => {
      ref?.removeEventListener('scroll', handleOnScroll);
    };
  }, [
    dismissOnScroll,
    isContextMenuOpen,
    refForScrollContainer,
    setIsContextMenuOpen,
  ]);
  return {
    contextMenuAnchorPosition,
    handleContextMenuEvent,
    isContextMenuOpen,
    setIsContextMenuOpen,
  };
}
